import { useCallback, useRef, useState } from 'react';

export interface AhuDragProps {
  draggable: boolean;
  onMouseDown: (e: React.MouseEvent) => void;
  onDragStart: (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragEnter: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  onDragEnd: (e: React.DragEvent) => void;
}

interface UseAhuDragOrderArgs {
  /** Row ids in their current (server cache) order. */
  ids: number[];
  /** Drag disabled without create+edit AHU permission or on a non-editable project (parity 151). */
  disabled: boolean;
  /** Persist the full new order (skipped when nothing moved — "no-movement drop sends nothing"). */
  onCommit: (orderedIds: number[]) => void;
  /** Extra dragstart side effect (dragging a main collapses version containers). */
  onDragStartExtra?: () => void;
  /** Drag-end side effect (clears the forced-collapsed state). */
  onDragEndExtra?: () => void;
  /**
   * Extra press-point selector that suppresses the drag. The mains usage passes
   * '.ahu-versions-container' — legacy stops a main drag for any mousedown
   * inside the versions container (ahus.js:642), which version rows live in.
   */
  suppressSelector?: string;
}

/**
 * Vertical sibling-swap drag reorder for the AHU list (B1.3 / parity 151) —
 * re-platform of the custom `draggables()` jQuery plugin (mousedown-driven,
 * ~6px start threshold, swap on row overlap) using the same HTML5 DnD
 * convention as the dashboard's useRowDragReorder. Used twice: over the
 * `.ahu-main-group` blocks and over each container's `.is-version` rows.
 *
 * Controls inside rows stay clickable: dragstart from a button/anchor/menu/
 * caret target — or an EDITABLE input — is suppressed (the legacy stops
 * mousedown propagation on those). A readonly/disabled input (the version
 * rows' name field) does NOT suppress the drag, so the row stays grabbable.
 */
export function useAhuDragOrder({
  ids,
  disabled,
  onCommit,
  onDragStartExtra,
  onDragEndExtra,
  suppressSelector,
}: UseAhuDragOrderArgs): {
  orderedIds: number[];
  dragProps: (id: number) => AhuDragProps;
  draggingId: number | null;
} {
  const [orderIds, setOrderIds] = useState<number[] | null>(null);
  const [draggingId, setDraggingId] = useState<number | null>(null);
  const dragIdRef = useRef<number | null>(null);
  // Native dragstart's e.target is the DRAG SOURCE (the deepest draggable
  // element), NOT the pressed element — so control suppression must key off
  // the preceding mousedown, exactly like the legacy delegated mousedown
  // suppressors (ahus.js:642,654).
  const pressTargetRef = useRef<HTMLElement | null>(null);

  // Server-order signature: when the cache order changes (refetch / silent
  // resort after commit), drop the local override.
  const sigRef = useRef('');
  const sig = ids.join(',');
  if (sigRef.current !== sig) {
    sigRef.current = sig;
    if (orderIds !== null) setOrderIds(null);
  }

  const orderedIds = (() => {
    if (!orderIds) return ids;
    const present = new Set(ids);
    const ordered = orderIds.filter((id) => present.has(id));
    const seen = new Set(ordered);
    return [...ordered, ...ids.filter((id) => !seen.has(id))];
  })();

  const dragProps = useCallback(
    (id: number): AhuDragProps => ({
      draggable: !disabled,
      // Record where the press landed (see pressTargetRef): dragstart itself
      // reports the drag source, not the pressed control.
      onMouseDown: (e: React.MouseEvent) => {
        pressTargetRef.current = e.target instanceof HTMLElement ? e.target : null;
      },
      onDragStart: (e: React.DragEvent) => {
        if (disabled) return;
        // Suppression keys off the PRESSED element (mousedown target), falling
        // back to the event target when no press was recorded inside this row.
        const source = e.currentTarget as HTMLElement;
        const pressed =
          pressTargetRef.current && source.contains(pressTargetRef.current)
            ? pressTargetRef.current
            : (e.target as HTMLElement);
        // Keep inner controls clickable / non-draggable (makeDraggable
        // mousedown stopImmediatePropagation list: input, button, a,
        // .dropdown-menu, .ahu-versions-toggle — ahus.js:642,654).
        // EXCEPTION: an input that is readonly/disabled (the version rows'
        // .ahu-name-input) can't take text selection, so it must NOT swallow
        // the drag — otherwise the row (mostly covered by that input) feels
        // undraggable. Only EDITABLE inputs (e.g. an in-place rename) suppress
        // the drag, mirroring the legacy behavior where the non-renaming name
        // input is pointer-events:none click-through (_ahus.scss:205-209) and
        // only the writable rename input can swallow the press.
        const input = pressed.closest('input');
        const editableInput =
          input instanceof HTMLInputElement && !input.readOnly && !input.disabled;
        if (
          editableInput ||
          pressed.closest('button, a, .dropdown-menu, .ahu-versions-toggle') ||
          (suppressSelector && pressed.closest(suppressSelector))
        ) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        // A version row sits INSIDE the draggable .ahu-main-group: without
        // stopPropagation the same dragstart bubbles to the group and ALSO
        // arms the mains hook (double commit + the mains' collapse side
        // effect hides the version rows mid-drag). Legacy equivalent: the
        // group-level mousedown suppressor for '.ahu-versions-container'
        // (ahus.js:642) keeps a version press from starting a main drag.
        e.stopPropagation();
        dragIdRef.current = id;
        e.dataTransfer.effectAllowed = 'move';
        try {
          e.dataTransfer.setData('text/plain', String(id));
        } catch {
          /* noop */
        }
        // Defer the `.dragging` flip (and the mains' collapse side effect) one
        // frame: setState here re-renders synchronously while the dragstart
        // dispatch is still on the stack, and the legacy project sheet's
        // `.dragging { pointer-events: none }` (styles.css:2044) then lands on
        // the source BEFORE Chromium latches the native drag — Chromium
        // CANCELS the drag (dragend fires with no dragenter ever, so rows
        // "cannot be reordered"; QA batch 3 #2). Applying the same class one
        // rAF later — after the drag is latched — is safe (probed: a deferred
        // opacity/z-index/pointer-events flip leaves dragenter/dragover
        // flowing). Legacy timing matches: its custom mousedown plugin only
        // styles the row after 6px of movement (draggables.js:37-47).
        requestAnimationFrame(() => {
          // Skip if the drag already ended (instant click-release).
          if (dragIdRef.current !== id) return;
          setDraggingId(id);
          onDragStartExtra?.();
        });
      },
      onDragOver: (e: React.DragEvent) => {
        if (disabled || dragIdRef.current == null) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
      },
      onDragEnter: (e: React.DragEvent) => {
        if (disabled || dragIdRef.current == null) return;
        e.preventDefault();
        const dragId = dragIdRef.current;
        if (dragId === id) return;
        setOrderIds((prev) => {
          const current = prev ?? orderedIds;
          const from = current.indexOf(dragId);
          const to = current.indexOf(id);
          if (from === -1 || to === -1 || from === to) return current;
          const next = [...current];
          next.splice(from, 1);
          next.splice(to, 0, dragId);
          return next;
        });
      },
      onDrop: (e: React.DragEvent) => {
        if (disabled) return;
        e.preventDefault();
      },
      onDragEnd: () => {
        const dragId = dragIdRef.current;
        dragIdRef.current = null;
        setDraggingId(null);
        onDragEndExtra?.();
        if (disabled || dragId == null) return;
        setOrderIds((prev) => {
          if (!prev) return prev;
          const changed = prev.some((pid, i) => ids[i] !== pid);
          // changedElem === drag → no actual movement, skip the POST.
          if (changed && prev.length) onCommit(prev);
          return prev;
        });
      },
    }),
    [disabled, orderedIds, ids, onCommit, onDragStartExtra, onDragEndExtra]
  );

  return { orderedIds, dragProps, draggingId };
}
